import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  Box, Typography, Chip, IconButton, Checkbox,
  Modal, Button, Fade, Backdrop,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ExpensesTableApplove } from '../Table';
import { ACTIVE_STATUSES } from '../../utils/constants/financeStatus.constant';
import * as actions from '../../redux/actions';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { DevWinBadge } from '../DevPageBadge';

export default function ApproverModal({ open, onClose, expenseIds, title, datePayment, allowedStatuses = null }) {
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const expenseRows = useSelector((state) => state.expenses?.rows || []);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [selectedRows, setSelectedRows] = useState([]);
  const [creating, setCreating] = useState(false);

  const idSet = React.useMemo(() => new Set(expenseIds.map(String)), [expenseIds]);
  const filter = React.useMemo(
    () => expenseRows.filter((e) => {
      if (!idSet.has(String(e._id))) return false;
      if (allowedStatuses && allowedStatuses.length > 0) {
        return allowedStatuses.includes(e.status);
      }
      return true;
    }),
    [expenseRows, idSet, allowedStatuses],
  );

  // APPROVE rows ที่ยังไม่มี payment (เลือกได้)
  const approveRows = React.useMemo(() => filter.filter((e) => e.status === 'APPROVE'), [filter]);

  const actorPayload = {
    approval_actor: me?.userData?.id,
    approval_actor_name: formatEmployeeThaiName(me?.userData),
  };

  const refresh = () => {
    dispatch(actions.expensesAll({ statuses: ACTIVE_STATUSES, size: 9999, page: 1 }));
    // fetch ALL payments (ไม่กรอง notStatus) เพื่อให้ FinanceSummary ยังเห็น SUCCESS payments ของวันนี้
    // SUCCESS payments จะย้ายไป Archive ก็ต่อเมื่อ datePayment < today ตาม logic ใน FinanceSummary/FinanceArchive
    dispatch(actions.paymentAll({ size: 9999, page: 1 }));
  };

  const makeHandler = (status) => async (id) => {
    await dispatch(actions.expensesPut(id, { status, ...actorPayload }));
    refresh();
  };

  const handlePreApprove = makeHandler('PREAPPROVE');
  const handleApprove = makeHandler('APPROVE');
  const handleApprove1 = makeHandler('APPROVE_1');
  const handleApprove2 = makeHandler('APPROVE_2');
  const handleApprove3 = makeHandler('APPROVE');
  const handleReject = makeHandler('REJECT');
  const handleReject1 = makeHandler('REJECT');
  const handleReject2 = makeHandler('REJECT_2');
  const handleReject3 = makeHandler('REJECT_3');
  const handleHold = makeHandler('HOLD');
  const handleReset = makeHandler('PENDING');

  const handleSuccess = async (id) => {
    await dispatch(actions.expensesPut(id, { status: 'SUCCESS' }));
    refresh();
  };
  const handleBillPickup = async (id) => {
    await dispatch(actions.expensesPut(id, { bill_pickup: true }));
    refresh();
  };
  const handleDetail = (id) => { window.open(`/finance/expenses/detail/${id}`, '_blank'); };
  const handleEdit = (id) => { window.open(`/finance/expenses/edit/${id}`, '_blank'); };

  const handleSelectAll = () => {
    if (selectedRows.length === approveRows.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(approveRows);
    }
  };

  const handleCreatePayment = async () => {
    const dateKey = dayjs(datePayment).format('YYYY-MM-DD');
    const pool = selectedRows.length > 0 ? selectedRows : approveRows;
    // กรองเฉพาะ expense ที่วันที่ตรงกับ DateRow ที่กด — ป้องกัน expense ต่างวันรวมกัน
    const targetRows = pool.filter((e) => dayjs(e.date || e.createdAt).format('YYYY-MM-DD') === dateKey);
    if (!targetRows.length) return;
    const confirm = await confirmAsync(`สร้าง Payment จาก ${targetRows.length} รายการ (status จะเปลี่ยนเป็น PREPARE)?`);
    if (!confirm) return;
    setCreating(true);
    try {
      await dispatch(actions.paymentPrepareFromIds({
        expenseIds: targetRows.map((e) => e._id),
        datePayment,
        creater: me?.userData?._id,
        create_by: formatEmployeeThaiName(me?.userData),
      }));
      // expensesAll ก่อน (sequential) เพื่อป้องกัน race condition:
      // expenseMap ต้อง update เป็น PREPARE ก่อนที่ paymentAll จะย้าย expense ออกจาก orphan
      // ใช้ Promise.resolve() เพื่อให้ TypeScript รู้ว่า return type คือ Promise
      await Promise.resolve(dispatch(actions.expensesAll({ statuses: ACTIVE_STATUSES, size: 9999, page: 1 })));
      await Promise.resolve(dispatch(actions.paymentAll({ size: 9999, page: 1 })));
      setSelectedRows([]);
    } catch (err) {
      console.error(err);
      showAlert('เกิดข้อผิดพลาด: ' + err.message, { status: err?.response?.status });
    } finally {
      setCreating(false);
    }
  };

  const allApproveSelected = approveRows.length > 0 && selectedRows.length === approveRows.length;
  const someApproveSelected = selectedRows.length > 0 && selectedRows.length < approveRows.length;

  return (
    <>
    <DevWinBadge num={139} name="ApproverModal" open={open} />
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '95vw',
            maxWidth: 1400,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Modal header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 3,
              py: 2,
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f8faff',
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
              รายการเบิกเงิน — {title || 'ทั้งหมด'}
            </Typography>
            <Chip label={`${filter.length} รายการ`} size="small" color="primary" variant="outlined" sx={{ mr: 2 }} />
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Create Payment toolbar — แสดงเมื่อมี APPROVE rows */}
          {approveRows.length > 0 && (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderBottom: '1px solid #bbf7d0',
                backgroundColor: '#f0fdf4',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {/* Checkbox เลือกทั้งหมด */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Checkbox
                  size="small"
                  checked={allApproveSelected}
                  indeterminate={someApproveSelected}
                  onChange={handleSelectAll}
                  sx={{ color: '#15803d', '&.Mui-checked': { color: '#15803d' } }}
                />
                <Typography variant="body2" fontWeight={500} color="#15803d">
                  {selectedRows.length > 0
                    ? `เลือกแล้ว ${selectedRows.length} / ${approveRows.length} รายการ`
                    : `เลือกทั้งหมด (${approveRows.length} อนุมัติแล้ว)`}
                </Typography>
              </Box>

              <Box sx={{ flexGrow: 1 }} />

              {/* วันที่รับเงิน (จาก L1 accordion) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">วันที่รับเงิน:</Typography>
                <Chip label={dayjs(datePayment).format('DD MMM BBBB')} size="small" sx={{ backgroundColor: '#bbf7d0', color: '#15803d', fontWeight: 600 }} />
              </Box>

              {/* ปุ่มสร้าง Payment */}
              <Button
                variant="contained"
                size="small"
                disabled={creating}
                onClick={handleCreatePayment}
                sx={{
                  backgroundColor: '#15803d',
                  '&:hover': { backgroundColor: '#166534' },
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                {creating ? 'กำลังสร้าง...' : `สร้าง Payment${selectedRows.length > 0 ? ` (${selectedRows.length})` : ` (ทั้งหมด ${approveRows.length})`}`}
              </Button>
            </Box>
          )}

          {/* Table */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
            <ExpensesTableApplove
              expenses={{ rows: filter }}
              page={page}
              size={size}
              setPage={setPage}
              setSize={setSize}
              handleEdit={handleEdit}
              handleDelete={() => {}}
              filter={filter}
              handlePreApprove={handlePreApprove}
              handleApprove={handleApprove}
              handleApprove1={handleApprove1}
              handleApprove2={handleApprove2}
              handleApprove3={handleApprove3}
              handleReject={handleReject}
              handleReject1={handleReject1}
              handleReject2={handleReject2}
              handleReject3={handleReject3}
              handleSuccess={handleSuccess}
              handleBillPickup={handleBillPickup}
              handleDetail={handleDetail}
              handleOpenPay={() => {}}
              handleAddMultiPayment={() => {}}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              handleHold={handleHold}
              handleReset={handleReset}
              handleOpenPrepareModal={() => {}}
              handlePrepareStatus={() => {}}
              handleOpenDetailModal={() => {}}
              me={me}
              info={info}
            />
          </Box>
        </Box>
      </Fade>
    </Modal>
    </>
  );
}
